// Allowlist прод-адресов API: какие api_url приложению вообще разрешено
// применять (persist после перезагрузки, api_urls[region] из ответов сервера).
//
// Инцидент 2026-08-26: клиенту уехал APK со стендовым бандлом, и приложение
// «прилипло» к стенду — каждый ответ стенда снова возвращал staging api_urls и
// переустанавливал baseURL (DEC-06 для веба, но для установленного приложения —
// самоподдерживающийся замок). Этот модуль закрывает вторую половину дыры:
// даже если в персисте или в ответе оказался адрес чужого окружения, сборка его
// не применит.
//
// Чьи адреса доверенные:
//   1. Origin собственного build-time API (VUE_APP_API_URL данной сборки) —
//      поэтому staging-сборка продолжает работать со стендом, а прод — с продом.
//      DEC-06 не нарушается: каждая сборка остаётся в своём окружении.
//   2. Origin'ы из VUE_APP_TRUSTED_API_ORIGINS (через запятую) — это ход для
//      разделения RU/EU: прод-сборка одна, канонических региональных API
//      несколько. При запуске EU в .env.production добавляется origin второго
//      региона, и переключение по api_urls из региональной механики FT-018
//      работает как задумано. Без записи в allowlist переключение на новый
//      регион молча не произойдёт — поэтому call sites обязаны логировать
//      отклонение console.error'ом (видно на приёмке региона сразу).
//
// Сравнение — строго по origin через originOf (@/utils/apiOrigin): тот же
// парсер, что решает судьбу JWT в заголовках. Второй канон разбора URL не
// заводится (см. комментарии в apiOrigin.js, почему вхождение подстроки — дыра).
//
// Development не проходит через этот модуль вовсе: dev-ветки мутаций и геттера
// пришпиливают localhost ДО проверки (см. store.js), их поведение не менялось.

import { originOf } from "@/utils/apiOrigin";

// Идентификатор ячейки приходит в OAuth-ссылке, но адресом никогда не является.
// Значения ниже и VUE_APP_API_REGION_URLS встраиваются Vue CLI в бандл на сборке;
// даже для известного ключа итоговый origin обязан пройти общий trusted allowlist.
const BUILT_IN_API_REGIONS = Object.freeze({
  ru: "https://rentprog.net",
  staging: "https://rentprog.pro",
});

function configuredApiRegions(env) {
  const regions = { ...BUILT_IN_API_REGIONS };
  String(env.VUE_APP_API_REGION_URLS || "")
    .split(",")
    .forEach((entry) => {
      const separator = entry.indexOf("=");
      if (separator < 1) return;
      const key = entry.slice(0, separator).trim();
      const url = entry.slice(separator + 1).trim();
      if (/^[a-z][a-z0-9_-]*$/.test(key) && originOf(url)) regions[key] = url;
    });
  return regions;
}

// Список доверенных origin'ов для данной сборки. defaultBase передаётся
// вызывающим (DEFAULT_API_BASE из utils/apiBase.js либо boot-time дефолт из
// main.js) — сам модуль ничего не импортирует из apiBase, чтобы не менять
// порядок инициализации бутстрапа.
export function trustedApiOrigins(defaultBase, env = process.env) {
  const origins = [];
  const remember = (value) => {
    const origin = originOf(value);
    if (origin && origins.indexOf(origin) === -1) origins.push(origin);
  };
  remember(defaultBase);
  String(env.VUE_APP_TRUSTED_API_ORIGINS || "")
    .split(",")
    .forEach((entry) => remember(entry.trim()));
  return origins;
}

// «Этому api_url можно доверить весь трафик приложения?» Неразобираемый или
// пустой URL — нет: политика та же, что в apiOrigin.js («не доказано» ≠ «наш»).
export function isTrustedApiUrl(url, defaultBase, env = process.env) {
  const origin = originOf(url);
  if (!origin) return false;
  return trustedApiOrigins(defaultBase, env).indexOf(origin) !== -1;
}

export function apiBaseForCell(cell, defaultBase, env = process.env) {
  const key = typeof cell === "string" ? cell.trim() : "";
  if (!/^[a-z][a-z0-9_-]*$/.test(key)) return null;

  const regions = configuredApiRegions(env);
  if (!Object.prototype.hasOwnProperty.call(regions, key)) return null;

  // Локальный Rails всё равно передаёт canonical key (обычно ru), но OAuth
  // endpoints обязаны остаться на API_URL текущего dev-сервера.
  if (env.NODE_ENV === "development") return defaultBase;

  const candidate = regions[key];
  return isTrustedApiUrl(candidate, defaultBase, env) ? candidate : null;
}
